import type { StudentContractInfo } from '@/types/api'
import type { User } from '@/types/user'

/**
 * R12-02 — regras puras da faixa "Assine seu contrato" (doc: medhunter-api/docs/autentique-contrato.md).
 *
 * A API decide quem recebe contrato; aqui so evitamos consultar `/contract` para quem
 * nunca recebe (admin, instrutor, sem plano ou so com plano gratuito).
 */

export type ContractNoticeKind = 'generate' | 'sign' | 'rejected'

export function shouldCheckContract(user: Pick<User, 'role' | 'plan_access'> | null | undefined): boolean {
  if (!user || user.role !== 'student') return false
  const access = user.plan_access
  if (!access || access.full_access) return false
  return access.plans.some((plan) => plan.free !== true)
}

/** O que a faixa mostra; null = nada (indisponivel ou ja assinado). */
export function contractNoticeKind(info: StudentContractInfo | undefined): ContractNoticeKind | null {
  if (!info || !info.available) return null
  if (info.status === 'signed') return null
  if (info.status === 'pending' && info.sign_url) return 'sign'
  if (info.status === 'rejected') return 'rejected'
  return 'generate'
}

/** "Assine o contrato do Plano Anual." (sem nome do plano: "Assine seu contrato."). */
export function contractNoticeTitle(info: StudentContractInfo | undefined): string {
  const plan = info && info.available ? info.plan_name?.trim() : null
  return plan ? `Assine o contrato do ${plan}.` : 'Assine seu contrato.'
}

export function needsCpf(info: StudentContractInfo | undefined): boolean {
  return Boolean(info && info.available && !info.cpf_on_file)
}

export function cpfDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 11)
}

/** Mascara 000.000.000-00 enquanto digita. */
export function formatCpf(value: string): string {
  const digits = cpfDigits(value)
  return digits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2')
}

/** Mesma conta dos digitos verificadores do backend (Contracts::Cpf). */
export function isValidCpf(value: string): boolean {
  const digits = cpfDigits(value)
  if (digits.length !== 11 || new Set(digits).size === 1) return false
  const numbers = digits.split('').map(Number)
  return checkDigit(numbers.slice(0, 9)) === numbers[9] && checkDigit(numbers.slice(0, 10)) === numbers[10]
}

function checkDigit(numbers: number[]): number {
  const weight = numbers.length + 1
  const sum = numbers.reduce((total, number, index) => total + number * (weight - index), 0)
  const rest = (sum * 10) % 11
  return rest === 10 ? 0 : rest
}
